/* eslint no-console: 0 */
/* global axios */
import ApiClient from '../ApiClient';

export const buildCreatePayload = ({
  message,
  isPrivate,
  contentAttributes,
  echoId,
  files,
  ccEmails = '',
  bccEmails = '',
  toEmails = '',
  templateParams,
  isVoiceMessage = false,
}) => {
  let payload;
  if (files && files.length !== 0) {
    payload = new FormData();
    if (message) {
      payload.append('content', message);
    }
    files.forEach(file => {
      payload.append('attachments[]', file);
    });
    payload.append('private', isPrivate);
    payload.append('echo_id', echoId);
    payload.append('cc_emails', ccEmails);
    payload.append('bcc_emails', bccEmails);

    if (toEmails) {
      payload.append('to_emails', toEmails);
    }
    if (contentAttributes) {
      payload.append('content_attributes', JSON.stringify(contentAttributes));
    }
    if (isVoiceMessage) {
      payload.append('is_voice_message', true);
    }
  } else {
    payload = {
      content: message,
      private: isPrivate,
      echo_id: echoId,
      content_attributes: contentAttributes,
      cc_emails: ccEmails,
      bcc_emails: bccEmails,
      to_emails: toEmails,
      template_params: templateParams,
    };
  }
  return payload;
};

class MessageApi extends ApiClient {
  constructor() {
    super('conversations', { accountScoped: true });
  }

  create({
    conversationId,
    message,
    private: isPrivate,
    contentAttributes,
    echo_id: echoId,
    files,
    ccEmails = '',
    bccEmails = '',
    toEmails = '',
    templateParams,
    isVoiceMessage = false,
  }) {
    return axios({
      method: 'post',
      url: `${this.url}/${conversationId}/messages`,
      data: buildCreatePayload({
        message,
        isPrivate,
        contentAttributes,
        echoId,
        files,
        ccEmails,
        bccEmails,
        toEmails,
        templateParams,
        isVoiceMessage,
      }),
    });
  }

  delete(conversationID, messageId) {
    return axios.delete(`${this.url}/${conversationID}/messages/${messageId}`);
  }

  telegramReactions(conversationID) {
    return axios.get(`${this.url}/${conversationID}/telegram_reactions`);
  }

  react(conversationID, messageId, emoji) {
    return axios.post(
      `${this.url}/${conversationID}/messages/${messageId}/reaction`,
      { emoji }
    );
  }

  loadTelegramHistory(conversationID, beforeId) {
    return axios.post(`${this.url}/${conversationID}/telegram_history`, {
      before_id: beforeId,
    });
  }

  // Operator tickets of the chat (TOPS-213).
  operatorTickets(conversationID) {
    return axios.get(`${this.url}/${conversationID}/operator_tickets`);
  }

  createOperatorTicket(conversationID, messageId, title) {
    return axios.post(`${this.url}/${conversationID}/operator_tickets`, {
      message_id: messageId,
      title,
    });
  }

  operatorTicket(conversationID, ticketId) {
    return axios.get(
      `${this.url}/${conversationID}/operator_tickets/${ticketId}`
    );
  }

  updateOperatorTicket(conversationID, ticketId, changes) {
    return axios.patch(
      `${this.url}/${conversationID}/operator_tickets/${ticketId}`,
      changes
    );
  }

  removeFromOperatorTicket(conversationID, ticketId, messageId) {
    return axios.delete(
      `${this.url}/${conversationID}/operator_tickets/${ticketId}/remove_message`,
      { params: { message_id: messageId } }
    );
  }

  addOperatorTicketNote(conversationID, ticketId, content) {
    return axios.post(
      `${this.url}/${conversationID}/operator_tickets/${ticketId}/notes`,
      { content }
    );
  }

  deleteOperatorTicketNote(conversationID, ticketId, noteId) {
    return axios.delete(
      `${this.url}/${conversationID}/operator_tickets/${ticketId}/notes/${noteId}`
    );
  }

  addToOperatorTicket(conversationID, ticketId, messageId) {
    return axios.post(
      `${this.url}/${conversationID}/operator_tickets/${ticketId}/add_message`,
      { message_id: messageId }
    );
  }

  fetchTelegramFile(conversationID, messageId) {
    return axios.post(
      `${this.url}/${conversationID}/messages/${messageId}/telegram_file`
    );
  }

  updateContent(conversationID, messageId, content) {
    return axios.patch(
      `${this.url}/${conversationID}/messages/${messageId}/update_content`,
      { content }
    );
  }

  retry(conversationID, messageId) {
    return axios.post(
      `${this.url}/${conversationID}/messages/${messageId}/retry`
    );
  }

  getPreviousMessages({ conversationId, after, before }) {
    const params = { before };
    if (after && Number(after) !== Number(before)) {
      params.after = after;
    }
    return axios.get(`${this.url}/${conversationId}/messages`, { params });
  }

  translateMessage(conversationId, messageId, targetLanguage) {
    return axios.post(
      `${this.url}/${conversationId}/messages/${messageId}/translate`,
      {
        target_language: targetLanguage,
      }
    );
  }
}

export default new MessageApi();
